// Copyright (c) Docugami, Inc. All rights reserved.

import { Icon, Link, Spinner } from "@fluentui/react";
import { useDocSetValidity } from "components/cluster_management/presentation/DocSetPicker";
import { Catalog, ICatalogItemGroup } from "components/myContentExplorer/catalog/Catalog";
import { PreviewText } from "components/myContentExplorer/catalog/CatalogPreview";
import { ChatView, ChatViewProps } from "components/chat/ChatView";
import { useFeatureFlag } from "hooks/FeatureFlags";
import { useDeepLinkInitializer } from "hooks/deepLinkInitializer";
import { NavigationType, useNavigationService } from "hooks/navigationService";
import { useAppSelector } from "hooks/redux";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { selectIsLimitedOrganization } from "store/AccountInfoHelpers";
import { createSelectOrgSampleClusters, useGetOrganizationSamplesQuery } from "store/clusters/samplesSlice";
import { FeatureFlags } from "store/featureFlags/types";
import { mapVisibleUserClusters } from "store/state_helpers";
import styled from "styled-components";
import { TelemetryPageView } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";

const selectOrgSampleClusters = createSelectOrgSampleClusters();
export const ChatPage = (): JSX.Element => {
    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    const chatEnabled = useFeatureFlag(FeatureFlags.ChatFeature);

    useDeepLinkInitializer();
    useGetOrganizationSamplesQuery();

    useEffect(() => {
        Telemetry.trackPageView(TelemetryPageView.Chat);
    }, []);

    const [selectedDocSetId, setSelectedDocSetId] = useState(queryParams.get('docSetId') ?? '');
    const userDocSets = useAppSelector(state => mapVisibleUserClusters(state.clusters));
    const sampleDocSets = useAppSelector(state => selectOrgSampleClusters(state));
    const isFetched = useAppSelector(state => state.clusters.fetched);

    const navService = useNavigationService();

    const isExpired = useAppSelector(state => selectIsLimitedOrganization(state));

    useEffect(() => {
        const queryDocSetId = queryParams.get('docSetId') ?? '';
        if (queryDocSetId !== selectedDocSetId) {
            setSelectedDocSetId(queryDocSetId);
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [queryParams])

    useEffect(() => {
        if (!chatEnabled || isExpired) {
            navService.navToHomePage(NavigationType.PushNewPage);
        }
    }, [chatEnabled, isExpired, navService])

    const items = useMemo(() => {
        const items: ICatalogItemGroup[] = [];
        if (!!userDocSets.length) {
            items.push({
                id: 'user_docsets',
                name: 'Your Doc Sets',
                items: userDocSets.map(docSet => ({
                    id: docSet.id!,
                    name: docSet.label!,
                    description: <ChatPreviewView docSetId={docSet.id!} />
                }))
            })
        }

        if (!!sampleDocSets.length) {
            items.push({
                id: 'sample_docsets',
                name: 'Sample Doc Sets',
                items: sampleDocSets.map(docSet => ({
                    id: docSet.id!,
                    name: docSet.label!,
                    description: <ChatPreviewView docSetId={docSet.id!} />
                }))
            })
        }

        return items;
    }, [userDocSets, sampleDocSets])

    return (
        <Catalog
            onSelectItem={(id) => {
                queryParams.delete('docSetId');
                if (!!id) {
                    queryParams.append('docSetId', id);
                }
                history.push({ ...location, search: queryParams.toString() })
            }}
            selectedItemId={selectedDocSetId}
            title="Chat"
            groupItems={items}
            placeholderText={"Welcome to chat, select a doc set to begin!"}
            isLoading={!isFetched} />
    )
}

const CenteredPreviewText = styled(PreviewText)`
    text-align: center;
`;

interface ChatPreviewViewProps extends ChatViewProps {
}

const ChatPreviewView = (props: ChatPreviewViewProps): JSX.Element => {
    const { getDeepLinkToDocSetPage } = useNavigationService();
    const docSetValidity = useDocSetValidity(props.docSetId);

    if (docSetValidity.isLoading || docSetValidity.status === undefined) {
        return <Spinner styles={{circle: { width: '80px', height: '80px', borderWidth: '8px' } }} />
    }

    if (!docSetValidity.isValid) {
        return (
            <CenteredPreviewText>
                <span>This doc set cannot be used for chat at the moment, as it is <b>{docSetValidity.status.label ?? 'not yet ready'}</b>.</span>
                <span>
                    If you would like to see its current status in detail, you can view your doc set <Link target="_blank" rel='noopener noreferrer' href={getDeepLinkToDocSetPage(props.docSetId)}>here <Icon iconName="NavigateExternalInline" /></Link>.
                </span>
                <br/>
                <br/>
                Otherwise, please pick a different doc set to chat with.
            </CenteredPreviewText>
        )
    }

    return <ChatView docSetId={props.docSetId} />
}